/**
 * O caminho de uma oportunidade pelo funil: quando entrou em cada etapa, de
 * onde veio, quem a moveu, e se terminou ganha ou perdida (24/09/2026).
 *
 * ── "Quem" nem sempre existe, e a resposta diz qual é o caso ─────────────
 *
 * A linha do tempo guarda TODA mudança de etapa, com a hora, mas não quem a
 * fez: o campo de ator dela é o dono da oportunidade no CRM. Quem moveu só é
 * conhecido quando o movimento passou pela nossa API, que o registra na
 * auditoria. Mudança feita direto no CRM, ou por automação, sai com `por`
 * nulo e `origem: 'fora'`, e a tela diz "pelo CRM ou por automação" em vez de
 * inventar um nome.
 */

import { z } from 'zod';
import { InstanteSchema } from './valores.js';

export const EtapaNoHistoricoSchema = z
  .object({
    valor: z.string(),
    /** O nome que a tela mostra. O próprio valor quando o nome não é conhecido. */
    rotulo: z.string(),
  })
  .strict();

export const PassoNoFunilSchema = z
  .object({
    quando: InstanteSchema,
    /** `criada` é a entrada no funil; `mudanca`, a passagem de uma etapa a outra. */
    tipo: z.enum(['criada', 'mudanca']),
    de: EtapaNoHistoricoSchema.nullable(),
    para: EtapaNoHistoricoSchema,
    /** Ganho ou perdido quando a etapa de destino é final classificada (057). */
    resultado: z.enum(['ganho', 'perdido']).nullable(),
    /** O nome de quem fez, quando se sabe. */
    por: z.string().nullable(),
    /** `pessoa`: feito pela equipe no CliqCRM. `fora`: no CRM, ou por automação. */
    origem: z.enum(['pessoa', 'fora']),
    /** O motivo e a observação, quando o passo foi para uma perda (058). */
    perda: z
      .object({ motivo: z.string().nullable(), observacao: z.string().nullable() })
      .strict()
      .nullable(),
  })
  .strict();

export type PassoNoFunil = z.infer<typeof PassoNoFunilSchema>;

export const HistoricoDaOportunidadeSchema = z
  .object({
    /** Do mais recente ao mais antigo, que é a ordem em que se lê. */
    itens: z.array(PassoNoFunilSchema),
  })
  .strict();

export type HistoricoDaOportunidade = z.infer<typeof HistoricoDaOportunidadeSchema>;
